import React, { useState } from "react";

// internal imports
import "./signup.css";
import colors from "../../../theme";
import { signUp } from "../../../services/api";
import CopyRight from "../../copyright/CopyRight";
import SnackbarComp from "../../Snackbar/SnackbarComp";
import RSP_LOGO_RG from "../../../assets/RSP_LOGO_RG.png";
import version from "../../../../package.json";

// external imports
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import CssBaseline from "@mui/material/CssBaseline";
import TextField from "@mui/material/TextField";
import FormControlLabel from "@mui/material/FormControlLabel";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import Container from "@mui/material/Container";
import CircularProgress from "@mui/material/CircularProgress";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import IconButton from "@mui/material/IconButton";
import FormControl from "@mui/material/FormControl";
import OutlinedInput from "@mui/material/OutlinedInput";
import InputLabel from "@mui/material/InputLabel";
import InputAdornment from "@mui/material/InputAdornment";

import { useNavigate } from "react-router-dom";
import { formControlInputStyle } from "../../../styles/formControlStyles";

export default function SignUp() {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
  });

  const [errors, setErrors] = useState({});
  const [error, setError] = useState(null);
  const [statusCode, setStatusCode] = useState(null);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(true);
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.firstName) {
      newErrors.firstName = "First name is required";
    } else if (formData.firstName.length > 50) {
      newErrors.firstName = "First name must be less than 50 characters";
    }

    if (!formData.lastName) {
      newErrors.lastName = "Last name is required";
    } else if (formData.lastName.length > 50) {
      newErrors.lastName = "Last name must be less than 50 characters";
    }

    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email) {
      newErrors.email = "Email is required";
    } else if (!emailPattern.test(formData.email)) {
      newErrors.email = "Invalid email address";
    }

    const passwordPattern = /^(?=.*[A-Z])(?=.*\d)(?=.*[~!@#$%^&*()_\-+=\[\]{}\\/|<>?])[A-Za-z\d~!@#$%^&*()_\-+=\[\]{}\\/|<>?]{8,}$/; // eslint-disable-line
    if (!formData.password) {
      newErrors.password = "Password is required";
    } else if (!passwordPattern.test(formData.password)) {
      newErrors.password =
        "Password must be at least 8 characters long with at least one numeric, one special character, and one capital letter";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleExternalLinkClick = (event) => {
    event.stopPropagation();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!agreedToTerms) {
      return;
    }
    setIsLoading(true);
    if (validateForm()) {
      const { email, firstName, lastName, password } = formData;
      try {
        const pairURL = localStorage.getItem('currentUrl');
        let chargePointId = null;
        if (pairURL) {
          chargePointId = pairURL.split('/').pop();
        }
        const response = await signUp({ email, firstName, lastName, password, chargePointId }); // Call signUp function from api.js

        if (response.success) {
          localStorage.removeItem('currentUrl');
          navigate("/check-email", {
            state: {
              email: email,
              text: "Just click on the link in that email to complete your signup.",
            },
          });
        } else {
          setError(response.message);
          setStatusCode(response.status);
          setOpenSnackbar(true);
        }
      } catch (error) {
        console.error("SignUp error:", error);
        setError(error.message || "An error occurred");
        setStatusCode(error.status || 500);
        setOpenSnackbar(true);
      } finally {
        setIsLoading(false);
      }
    } else {
      setIsLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };
  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const handleLoginWithGoogle = async (event) => {
    event.preventDefault(); // Prevent default form submission behavior
    if (!agreedToTerms) {
      return;
    }
    try {
      window.location.href = `${process.env.REACT_APP_API_URL}/auth/google`;
    } catch (error) {
      console.error("Google login error:", error);
    }
  };

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Container component="main" maxWidth="xs">
        <CssBaseline />
        <Box
          sx={{
            marginTop: 6,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Box sx={{ marginBottom: "10px" }}>
            <img src={RSP_LOGO_RG} alt="RSP Logo" width={300} height={50} />
          </Box>

          <Typography
            variant="caption"
            gutterBottom
            sx={{ fontWeight: "normal", textAlign: "center", marginBottom: "36px" }}
          >
            v{version.version} {process.env.REACT_APP_API_URL.includes("test") ? "(Test)" : ""}
          </Typography>

          <Box
            component="form"
            noValidate
            onSubmit={handleSubmit}
            sx={{ mt: 3 }}
          >
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  autoComplete="given-name"
                  name="firstName"
                  required
                  fullWidth
                  id="firstName"
                  label="First Name"
                  value={formData.firstName}
                  onChange={handleChange}
                  error={!!errors.firstName}
                  helperText={errors.firstName}
                  InputProps={{
                    style: {
                      borderRadius: 20,
                      marginBottom: 5,
                      height: "3.5rem",
                    },
                  }}
                  sx={formControlInputStyle}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  id="lastName"
                  label="Last Name"
                  name="lastName"
                  autoComplete="family-name"
                  value={formData.lastName}
                  onChange={handleChange}
                  error={!!errors.lastName}
                  helperText={errors.lastName}
                  InputProps={{
                    style: {
                      borderRadius: 20,
                      marginBottom: 5,
                      height: "3.5rem",
                    },
                  }}
                  sx={formControlInputStyle}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  id="email"
                  label="Email"
                  name="email"
                  autoComplete="email"
                  value={formData.email}
                  onChange={handleChange}
                  error={!!errors.email}
                  helperText={errors.email}
                  InputProps={{
                    style: {
                      borderRadius: 20,
                      marginBottom: 5,
                      height: "3.5rem",
                    },
                  }}
                  sx={formControlInputStyle}
                />
              </Grid>
              <Grid item xs={12} sx={{ marginBottom: "20px" }}>
                <FormControl
                  fullWidth
                  variant="outlined"
                  error={!!errors.password}
                  sx={formControlInputStyle}
                >
                  <InputLabel htmlFor="outlined-adornment-password">
                    Password
                  </InputLabel>
                  <OutlinedInput
                    fullWidth
                    name="password"
                    id="outlined-adornment-password"
                    type={!showPassword ? "text" : "password"}
                    value={formData.password}
                    onChange={handleChange}
                    endAdornment={
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleClickShowPassword}
                          onMouseDown={handleMouseDownPassword}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    }
                    label="Password"
                    style={{ borderRadius: 20, height: "3.5rem" }}
                  />
                  {errors.password && (
                    <div style={{ color: "red", fontSize: "0.8em" }}>
                      {errors.password}
                    </div>
                  )}
                </FormControl>
              </Grid>
            </Grid>

            <FormControlLabel
              sx={{ alignItems: "flex-start", mb: 2, ml: 0 }}
              control={
                <Checkbox
                  checked={agreedToTerms}
                  onChange={(e) => setAgreedToTerms(e.target.checked)}
                  sx={{
                    color: colors.secondary,
                    "&.Mui-checked": { color: colors.secondary },
                    pt: 0,
                  }}
                />
              }
              label={
                <Typography variant="body2" sx={{ color: "text.primary" }}>
                  I agree to the{" "}
                  <Link
                    href={"https://readysteadyplug.com.au/terms-conditions"}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="body2"
                    onClick={handleExternalLinkClick}
                    sx={{
                      fontWeight: "bold",
                      color: colors.secondary,
                      verticalAlign: "baseline",
                    }}
                  >
                    Terms & Conditions
                  </Link>{" "}
                  and{" "}
                  <Link
                    href={"https://readysteadyplug.com.au/privacy-policy"}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="body2"
                    onClick={handleExternalLinkClick}
                    sx={{
                      fontWeight: "bold",
                      color: colors.secondary,
                      verticalAlign: "baseline",
                    }}
                  >
                    Privacy Policy
                  </Link>
                </Typography>
              }
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{
                fontWeight: "bold",
                borderRadius: "20px",
                height: "3.5rem",
                backgroundColor: colors.primary,
                "&:hover": {
                  backgroundColor: colors.hoverButton,
                },
                "&.Mui-disabled": {
                  backgroundColor: colors.primary,
                  opacity: 0.5,
                  color: "rgba(0, 0, 0, 0.5)",
                },
              }}
              disabled={isLoading || !agreedToTerms}
            >
              {isLoading ? <CircularProgress size={24} /> : "Sign Up"}
            </Button>
            <hr className="my-4"></hr>

            <Button
              fullWidth
              variant="contained"
              data-mdb-button-init
              data-mdb-ripple-init
              className="btn btn-lg btn-block btn-primary btn-red custom-btn mb-3 google-button social-button"
              onClick={handleLoginWithGoogle}
              disabled={!agreedToTerms}
            >
              <i className="fab fa-google me-2 text-purple-500 rounded"></i>
              Sign in/up with Google
            </Button>

            {/* <Button
              fullWidth
              variant="contained"
              data-mdb-button-init
              data-mdb-ripple-init
              className="btn btn-lg btn-block btn-black social-button"
              id="apple-button"
            >
              <i className="fab fa-apple me-2"></i>Sign up with Apple
            </Button> */}

            <div className="container mt-3 mb-3">
              <div className="row">
                <div className="col">
                  <a
                    href="/login"
                    className="text-primary fw-bold d-block text-center signup-col"
                  >
                    Already have an account? Sign in
                  </a>
                </div>
              </div>
            </div>
          </Box>
        </Box>

        <CopyRight sx={{ mt: 4, mb: 4 }} />
      </Container>
      <SnackbarComp
        open={openSnackbar}
        onClose={handleCloseSnackbar}
        errorMessage={error}
        statusCode={statusCode}
      />
    </div>
  );
}
